import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { ShortDate } from "../components/ShortDate";
import { copyFor } from "../lib/platformSwitches";
import { useTRPC } from "../lib/trpc";

/**
 * Platform-wide switches, for an incident. Disabling one stops that action for **every tenant**
 * at once; a single tenant's pause lives on that tenant's own pages (Storefront, Settings).
 *
 * Switches read as the action they gate, and the state says whether we are running it — Enabled means we are
 * running it — there is no "kill" checkbox to misread under pressure.
 */
export function PlatformSwitchesPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const options = trpc.platform.switches.list.queryOptions();
  const query = useQuery(options);
  // Which row's confirm is open. AlertDialogAction is a plain Button — only Cancel wraps
  // Close — so a confirmed action has to close its own dialog. Left open, the row flips
  // underneath it and the dialog re-renders offering the opposite action.
  const [confirming, setConfirming] = useState<string | null>(null);

  const setSwitch = useMutation(
    trpc.platform.switches.set.mutationOptions({
      onSuccess: (saved) => {
        void queryClient.invalidateQueries({ queryKey: options.queryKey });
        const { label } = copyFor(saved.key);
        toast.success(saved.enabled ? `${label} enabled.` : `${label} disabled.`);
      },
    }),
  );

  const disabled = (query.data ?? []).filter((row) => !row.enabled);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Platform Switches</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Platform-wide switches for an incident. Disabling one stops that action for every tenant
          at once; pausing a single tenant is done from that tenant's own pages. A change reaches
          running servers within a few seconds.
        </p>
      </div>

      {query.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {query.error && <ErrorAlert message={query.error.message} />}

      {query.data && disabled.length > 0 && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">
              {disabled.length === 1
                ? "1 action is disabled"
                : `${disabled.length} actions are disabled`}
            </CardTitle>
            <CardDescription>
              {disabled.map((row) => copyFor(row.key).label).join(", ")} — stopped for every tenant.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {query.data && (
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
            <CardDescription>
              Each switch is independent. Disabling one leaves the rest enabled unless its
              description says otherwise.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col divide-y divide-border p-0">
            {query.data.map((row) => {
              const { label, gates, alsoDisables } = copyFor(row.key);
              return (
                <div
                  key={row.key}
                  className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{label}</span>
                      <Badge variant={row.enabled ? "secondary" : "destructive"}>
                        {row.enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{gates}</p>
                    {row.updatedAt && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Last changed <ShortDate value={row.updatedAt} />
                        {row.updatedBy ? ` by ${row.updatedBy}` : null}
                      </p>
                    )}
                  </div>

                  <AlertDialog
                    open={confirming === row.key}
                    onOpenChange={(open) => setConfirming(open ? row.key : null)}
                  >
                    <AlertDialogTrigger
                      render={
                        <Button
                          type="button"
                          variant={row.enabled ? "outline" : "default"}
                          size="sm"
                          disabled={setSwitch.isPending}
                        />
                      }
                    >
                      {row.enabled ? "Disable" : "Enable"}
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {row.enabled ? `Disable ${label} for every tenant?` : `Enable ${label}?`}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {row.enabled ? (
                            <>
                              {gates} {alsoDisables ? `${alsoDisables} ` : ""}
                              Servers already running pick this up within a few seconds.
                            </>
                          ) : (
                            <>
                              {label} runs again for every tenant. Any tenant-level pause stays as
                              it is.
                            </>
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            setConfirming(null);
                            setSwitch.mutate({ key: row.key, enabled: !row.enabled });
                          }}
                        >
                          {row.enabled ? `Disable ${label}` : `Enable ${label}`}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
